import Phaser from 'phaser';
import { GAME_FONT_FAMILY } from '@shared/constants';
import type { QuestView, QuestRewards, QuestObjectiveView } from '@shared/types/quest';
import { objectiveTarget, defaultObjectiveLabel } from '@shared/types/quest';
import { QUEST_DEFINITIONS } from '@shared/data/quests';
import type {
  QuestLogSyncMessage,
  QuestOfferMessage,
  QuestProgressMessage,
  QuestCompletedMessage,
} from '@shared/types/messages';
import { createCloseButton, createUIIcon } from '../../sprites/IconAtlases';
import type { QuestObjectiveType } from '@shared/types/quest';
import { Direction } from '@shared/types/entity';
import { getNPCTemplate } from '@shared/data/npcs';
import { getItemTemplate } from '@shared/data/items';
import { getBodySpriteKey, getBodyIdleFrame } from '../../sprites/BodySpriteManager';
import { getHeadSpriteKey, getHeadFrameForDirection } from '../../sprites/HeadSpriteManager';
import { QuestPinnedPanel } from './QuestPinnedPanel';
import {
  loadQuestPins,
  saveQuestPins,
  emptyQuestPinsState,
  MAX_PINNED_QUESTS,
  type QuestPinsState,
} from './QuestPins';

function withGameFont<T extends Phaser.Types.GameObjects.Text.TextStyle>(style?: T): T {
  return { fontFamily: GAME_FONT_FAMILY, resolution: 2, ...style } as T;
}

const DEPTH = 1260; // por encima del HUD (1000) y de los vendor panels (1250)
const COLOR_BG = 0x14130f;
const COLOR_BORDER = 0x8a7a4a;
const TXT = '#e8e0cc';
const TXT_DIM = '#b3a98c';
const TXT_DONE = '#77dd77';
const TXT_GOLD = '#ffd56b';
const TXT_TITLE = '#ffe9a8';

// === Skin del quest log (ventana gráfica). Assets en client/assets/ui/window/ y
// client/assets/ui/quests/. Frame `c_full_header` (2334×1514 nativo) escalado a
// WIN_W de display; regiones internas expresadas en px de display (tuneables contra
// el arte, igual idea que TargetPanel/Minimap). Ver docs/UI_SKINNING.md. ===
const WIN_NATIVE_W = 2334;
const WIN_NATIVE_H = 1514;
const WIN_W = 760; // ancho de display de la ventana
const WIN_H = Math.round((WIN_NATIVE_H / WIN_NATIVE_W) * WIN_W); // ~493 (mantiene aspecto)
const HEADER_H = Math.round(WIN_H * 0.1); // alto de la barra de header (display), tuneable
const BORDER = 14; // inset del contenido respecto del borde del frame
const LIST_W = Math.round((WIN_W - BORDER * 2) * 0.38); // ancho de la columna de lista
const ROW_H = 46;
const ROW_GAP = 8;
const CHECKBOX = 22;
const SEP_W = 14; // ancho de display del separador vertical

/** Icono (atlas UI) por tipo de objetivo: matar / conseguir / hablar / llegar. */
const OBJECTIVE_ICON: Record<QuestObjectiveType, string> = {
  kill: 'sword',
  kill_kingdom: 'sword',
  collect: 'loot',
  talk: 'chat',
  reach: 'map',
};

/** Conectores que se mantienen junto al verbo (peso normal) al separar la etiqueta. */
const OBJ_STOPWORDS = new Set([
  'a', 'al', 'un', 'una', 'unos', 'unas', 'el', 'la', 'los', 'las', 'de', 'del', 'con', 'hasta',
]);

/**
 * Separa la etiqueta del objetivo en `prefix` (verbo + conectores, peso normal) y
 * `name` (el resto, en negrita). Ej: "Derrotar a Lord Orco" → { "Derrotar a", "Lord Orco" }.
 */
function splitObjectiveLabel(desc: string): { prefix: string; name: string } {
  const tokens = desc.trim().split(/\s+/);
  if (tokens.length <= 1) return { prefix: '', name: desc };
  const prefixTokens = [tokens[0]];
  let i = 1;
  while (i < tokens.length - 1 && OBJ_STOPWORDS.has(tokens[i].toLowerCase())) {
    prefixTokens.push(tokens[i]);
    i++;
  }
  return { prefix: prefixTokens.join(' '), name: tokens.slice(i).join(' ') };
}

export interface QuestUICallbacks {
  onAccept(questId: string, npcId: string): void;
  onTurnIn(questId: string, npcId: string): void;
  onAbandon(questId: string): void;
  /** Character actual (para persistir pins/favorito por personaje en localStorage). */
  getCharacterId(): string | null;
}

/**
 * UI de quests (cliente): ventana flotante de quests FIJADAS (`QuestPinnedPanel`) +
 * diálogo de oferta/entrega en el NPC + quest log toggleable. Dueña del estado de
 * pin/favorito (persistido por personaje) y fuente única de la quest favorita, que
 * el World Map usa como destino de la ruta. Recibe los mensajes QUEST_* del server
 * y emite ACCEPT/TURN_IN/ABANDON vía callbacks. Ver docs/QUESTS_PINNED_TRACKER.md.
 */
export class QuestUI {
  private scene: Phaser.Scene;
  private cb: QuestUICallbacks;

  /** Estado del log: quests activas + ready (las completed no viajan). */
  private quests: QuestView[] = [];

  /**
   * IDs de quests ya ENTREGADAS (status completed). El server sí las manda en el
   * QUEST_LOG_SYNC (cachea todo lo aceptado); las guardamos aparte del array `quests`
   * (que sigue siendo sólo active/ready) para que el World Map pueda listar el catálogo
   * completo distinguiendo hechas/no hechas. Ver docs/WORLD_MAP.md.
   */
  private completedIds = new Set<string>();

  // Ventana flotante de quests fijadas (reemplaza el tracker de texto viejo).
  private pinnedPanel: QuestPinnedPanel;
  // Estado de pin/favorito/colapsado + posición, persistido por personaje.
  private pins: QuestPinsState = emptyQuestPinsState();
  private pinsCharId: string | null = null;

  // Diálogo de oferta/entrega (modal liviano centrado).
  private dialog: Phaser.GameObjects.Container | null = null;
  private dialogNpcId: string | null = null;
  private dialogQuest: QuestView | null = null;

  // Quest log (panel toggleable).
  private log: Phaser.GameObjects.Container | null = null;
  private logSelectedId: string | null = null;
  // TODO(debug): quest fake client-side elegida al abrir el log cuando no hay quests
  // reales, para poder ver/tunear el render sin aceptar una quest in-game. QUITAR.
  private debugQuest: QuestView | null = null;
  // Drag del log: el container se mueve por su offset; los hijos se renderizan en
  // coords absolutas centradas y el offset del container los reubica al lugar arrastrado.
  private logDragging = false;
  private logDragDX = 0;
  private logDragDY = 0;
  /** Top-left base (centrado) de la ventana, para clampear el drag dentro de pantalla. */
  private logBaseX = 0;
  private logBaseY = 0;
  /** Cache del bbox opaco por textura+frame (para encuadrar el mini-retrato del NPC). */
  private bboxCache = new Map<string, { cx: number; cy: number; w: number; h: number; fw: number; fh: number }>();
  // Scroll de la sección de objetivos (windowing por índice; título/desc quedan fijos).
  private objScroll = 0;
  /** Id de la quest cuyo scroll está vigente; al cambiar de selección reseteo el scroll. */
  private objScrollSelId: string | null = null;
  /** Métricas de la última región de objetivos (para rueda y drag del thumb), en coords base. */
  private scrollMetrics: { top: number; bottom: number; left: number; right: number; total: number; visible: number } | null = null;
  private thumbDragging = false;
  private thumbGrabDY = 0;

  constructor(scene: Phaser.Scene, callbacks: QuestUICallbacks) {
    this.scene = scene;
    this.cb = callbacks;

    // Ventana flotante de quests fijadas. El estado (pin/favorito/colapsado/pos)
    // se persiste por personaje; se carga perezosamente cuando hay characterId.
    this.pinnedPanel = new QuestPinnedPanel(scene, {
      onToggleFavorite: (id) => this.setFavoritedQuest(this.pins.favoritedId === id ? null : id),
      onToggleCollapse: (id) => this.toggleCollapse(id),
      onUnpin: (id) => this.removePin(id),
      onDragEnd: (x, y) => {
        this.ensurePins();
        this.pins.panelX = x;
        this.pins.panelY = y;
        this.persistPins();
      },
    });

    scene.scale.on(Phaser.Scale.Events.RESIZE, () => this.onResize());

    // Drag del quest log: el handle es el frame (header). Movemos el container.
    scene.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (this.thumbDragging && this.log) {
        this.dragThumbTo(p.y);
        return;
      }
      if (!this.logDragging || !this.log) return;
      const maxOffX = this.scene.scale.width - WIN_W - this.logBaseX;
      const maxOffY = this.scene.scale.height - WIN_H - this.logBaseY;
      const offX = Phaser.Math.Clamp(p.x - this.logDragDX, -this.logBaseX, Math.max(-this.logBaseX, maxOffX));
      const offY = Phaser.Math.Clamp(p.y - this.logDragDY, -this.logBaseY, Math.max(-this.logBaseY, maxOffY));
      this.log.setPosition(offX, offY);
    });
    scene.input.on('pointerup', () => {
      this.logDragging = false;
      this.thumbDragging = false;
    });

    // Rueda: scrollea SOLO la sección de objetivos si el puntero cae en su región.
    scene.input.on('wheel', (p: Phaser.Input.Pointer, _o: unknown, _dx: number, dy: number) => {
      if (!this.log || !this.scrollMetrics) return;
      const m = this.scrollMetrics;
      const ox = this.log.x;
      const oy = this.log.y;
      const inX = p.x >= m.left + ox && p.x <= m.right + ox;
      const inY = p.y >= m.top + oy && p.y <= m.bottom + oy;
      if (!inX || !inY) return;
      const max = Math.max(0, m.total - m.visible);
      const next = Phaser.Math.Clamp(this.objScroll + (dy > 0 ? 1 : -1), 0, max);
      if (next !== this.objScroll) {
        this.objScroll = next;
        this.rebuildLog();
      }
    });
  }

  /** Mapea la Y del puntero al offset de scroll mientras se arrastra el thumb. */
  private dragThumbTo(pointerY: number): void {
    if (!this.log || !this.scrollMetrics) return;
    const m = this.scrollMetrics;
    const max = Math.max(0, m.total - m.visible);
    if (max <= 0) return;
    const regionH = m.bottom - m.top;
    const thumbH = Math.max(24, regionH * (m.visible / m.total));
    const travel = regionH - thumbH;
    if (travel <= 0) return;
    // Y del tope del thumb en coords base (descontando el offset del container y el grab).
    const topBase = pointerY - this.log.y - this.thumbGrabDY;
    const frac = Phaser.Math.Clamp((topBase - m.top) / travel, 0, 1);
    const next = Math.round(frac * max);
    if (next !== this.objScroll) {
      this.objScroll = next;
      this.rebuildLog();
    }
  }

  // =========================================================================
  // MENSAJES DEL SERVER
  // =========================================================================

  /** Quests activas/ready del player (para el World Map). Copia defensiva. */
  public getActiveQuests(): QuestView[] {
    return this.quests.slice();
  }

  /** IDs de quests ya entregadas (para el catálogo del World Map). Set vivo (solo lectura). */
  public getCompletedQuestIds(): ReadonlySet<string> {
    return this.completedIds;
  }

  public onLogSync(msg: QuestLogSyncMessage): void {
    this.quests = msg.quests.filter((q) => q.status !== 'completed');
    // El sync es el estado COMPLETO: reconstruir el set de completadas desde cero.
    this.completedIds = new Set(
      msg.quests.filter((q) => q.status === 'completed').map((q) => q.id)
    );
    this.afterQuestsChanged();
    if (this.log) this.rebuildLog();
  }

  public onProgress(msg: QuestProgressMessage): void {
    this.upsertQuest(msg.quest);
    this.afterQuestsChanged();
    if (this.log) this.rebuildLog();
    // Si el diálogo está mostrando esta quest, refrescarlo (p.ej. pasó a ready).
    if (this.dialog && this.dialogQuest?.id === msg.quest.id) {
      this.dialogQuest = msg.quest;
      this.rebuildDialog();
    }
  }

  public onOffer(msg: QuestOfferMessage): void {
    this.dialogNpcId = msg.npcId;
    this.dialogQuest = msg.quest;
    this.openDialog();
  }

  public onCompleted(msg: QuestCompletedMessage): void {
    this.quests = this.quests.filter((q) => q.id !== msg.questId);
    this.completedIds.add(msg.questId);
    this.afterQuestsChanged();
    if (this.log) this.rebuildLog();
    if (this.dialog && this.dialogQuest?.id === msg.questId) this.closeDialog();
    this.toast(`Misión completada: ${msg.questName}`, this.rewardsLine(msg.rewards));
  }

  private upsertQuest(q: QuestView): void {
    const idx = this.quests.findIndex((x) => x.id === q.id);
    if (q.status === 'completed') {
      if (idx >= 0) this.quests.splice(idx, 1);
      this.completedIds.add(q.id);
      return;
    }
    if (idx >= 0) this.quests[idx] = q;
    else this.quests.push(q);
  }

  // =========================================================================
  // QUESTS FIJADAS (pin) + FAVORITA (estrella → ruta del World Map)
  // =========================================================================

  /** Carga perezosa del estado de pins para el personaje actual. */
  private ensurePins(): void {
    const charId = this.cb.getCharacterId();
    if (charId && this.pinsCharId !== charId) {
      this.pins = loadQuestPins(charId);
      this.pinsCharId = charId;
    }
  }

  private persistPins(): void {
    const charId = this.cb.getCharacterId();
    if (charId) saveQuestPins(charId, this.pins);
  }

  /** Poda ids de quests que ya no existen (abandonadas/entregadas) + refresca el panel. */
  private afterQuestsChanged(): void {
    this.ensurePins();
    const alive = new Set(this.quests.map((q) => q.id));
    let changed = false;
    const pinned = this.pins.pinnedIds.filter((id) => alive.has(id));
    if (pinned.length !== this.pins.pinnedIds.length) {
      this.pins.pinnedIds = pinned;
      changed = true;
    }
    const collapsed = this.pins.collapsedIds.filter((id) => alive.has(id));
    if (collapsed.length !== this.pins.collapsedIds.length) {
      this.pins.collapsedIds = collapsed;
      changed = true;
    }
    if (this.pins.favoritedId && !alive.has(this.pins.favoritedId)) {
      this.pins.favoritedId = null;
      changed = true;
    }
    if (changed) this.persistPins();
    this.refreshPinnedPanel();
  }

  /** Quests fijadas resueltas a QuestView (en el orden de pineo). */
  private resolvePinnedQuests(): QuestView[] {
    this.ensurePins();
    const byId = new Map(this.displayQuests().map((q) => [q.id, q] as const));
    return this.pins.pinnedIds
      .map((id) => byId.get(id))
      .filter((q): q is QuestView => !!q);
  }

  private refreshPinnedPanel(): void {
    this.ensurePins();
    this.pinnedPanel.setData({
      quests: this.resolvePinnedQuests(),
      favoritedId: this.pins.favoritedId,
      collapsedIds: new Set(this.pins.collapsedIds),
      pos:
        this.pins.panelX !== null && this.pins.panelY !== null
          ? { x: this.pins.panelX, y: this.pins.panelY }
          : null,
    });
  }

  private isPinned(questId: string): boolean {
    this.ensurePins();
    return this.pins.pinnedIds.includes(questId);
  }

  /** Alterna fijar/desfijar una quest (máx 3). Llamado desde el pin del quest log. */
  private togglePin(questId: string): void {
    this.ensurePins();
    if (this.pins.pinnedIds.includes(questId)) {
      this.removePin(questId);
      return;
    }
    if (this.pins.pinnedIds.length >= MAX_PINNED_QUESTS) {
      this.toast('Solo podés fijar 3 misiones', '');
      return;
    }
    this.pins.pinnedIds.push(questId);
    this.persistPins();
    this.refreshPinnedPanel();
    if (this.log) this.rebuildLog();
  }

  /** Deja de fijar una quest (y limpia el favorito si era esa). */
  private removePin(questId: string): void {
    this.ensurePins();
    const idx = this.pins.pinnedIds.indexOf(questId);
    if (idx < 0) return;
    this.pins.pinnedIds.splice(idx, 1);
    if (this.pins.favoritedId === questId) this.pins.favoritedId = null;
    this.persistPins();
    this.refreshPinnedPanel();
    if (this.log) this.rebuildLog();
  }

  private toggleCollapse(questId: string): void {
    this.ensurePins();
    const idx = this.pins.collapsedIds.indexOf(questId);
    if (idx >= 0) this.pins.collapsedIds.splice(idx, 1);
    else this.pins.collapsedIds.push(questId);
    this.persistPins();
    this.refreshPinnedPanel();
  }

  /** Id de la quest favorita (destino de la ruta del World Map). Fuente única. */
  public getFavoritedQuestId(): string | null {
    this.ensurePins();
    return this.pins.favoritedId;
  }

  /** Setea la favorita (single-favorite). Auto-pinea si hay lugar. Persiste + refresca. */
  public setFavoritedQuest(id: string | null): void {
    this.ensurePins();
    if (id && !this.pins.pinnedIds.includes(id) && this.pins.pinnedIds.length < MAX_PINNED_QUESTS) {
      this.pins.pinnedIds.push(id); // que aparezca en el panel con su estrella
    }
    this.pins.favoritedId = id;
    this.persistPins();
    this.refreshPinnedPanel();
    if (this.log) this.rebuildLog();
  }

  /** Oculta el panel flotante mientras el World Map está abierto (evita solaparse). */
  public setMapOpen(open: boolean): void {
    this.pinnedPanel.setForceHidden(open);
  }

  // =========================================================================
  // DIÁLOGO DE OFERTA / ENTREGA
  // =========================================================================

  private openDialog(): void {
    this.closeDialog();
    this.dialog = this.scene.add.container(0, 0).setDepth(DEPTH).setScrollFactor(0);
    this.rebuildDialog();
  }

  private rebuildDialog(): void {
    if (!this.dialog || !this.dialogQuest) return;
    this.dialog.removeAll(true);

    const q = this.dialogQuest;
    const W = 420;
    const cx = this.scene.scale.width / 2;
    const cy = this.scene.scale.height / 2;

    // Detectar si esta quest ya está en el log (→ modo entrega/seguimiento) o es oferta nueva.
    const inLog = this.quests.find((x) => x.id === q.id);
    const isReady = (inLog?.status ?? q.status) === 'ready';
    const alreadyActive = !!inLog && !isReady;

    const lines: Array<{ t: string; c: string; size?: string }> = [];
    lines.push({ t: q.nameEs, c: TXT_TITLE, size: '17px' });
    lines.push({ t: q.descriptionEs, c: TXT, size: '12px' });
    lines.push({ t: 'Objetivos:', c: TXT_DIM, size: '12px' });
    for (const o of (inLog ?? q).objectives) {
      const mark = o.complete ? '✔' : '•';
      const count = o.target > 1 ? ` ${o.current}/${o.target}` : '';
      lines.push({ t: `  ${mark} ${o.descEs}${count}`, c: o.complete ? TXT_DONE : TXT, size: '12px' });
    }
    const rl = this.rewardsLine(q.rewards);
    if (rl) {
      lines.push({ t: 'Recompensas:', c: TXT_DIM, size: '12px' });
      lines.push({ t: `  ${rl}`, c: TXT_GOLD, size: '12px' });
    }

    // Medir alto.
    const texts: Phaser.GameObjects.Text[] = [];
    let y = 16;
    for (const ln of lines) {
      const txt = this.scene.add
        .text(0, y, ln.t, withGameFont({ fontSize: ln.size ?? '12px', color: ln.c, wordWrap: { width: W - 32 } }))
        .setOrigin(0, 0)
        .setScrollFactor(0);
      texts.push(txt);
      y += txt.height + 5;
    }
    const btnRowY = y + 6;
    const H = btnRowY + 44;

    const bx = cx - W / 2;
    const by = cy - H / 2;

    const bg = this.scene.add
      .rectangle(bx, by, W, H, COLOR_BG, 0.97)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLOR_BORDER, 1)
      .setScrollFactor(0);
    this.dialog.add(bg);
    for (const txt of texts) {
      txt.setPosition(bx + 16, by + txt.y);
      this.dialog.add(txt);
    }

    // Botón primario: Entregar (ready) / Aceptar (oferta nueva) / nada si solo seguimiento.
    if (isReady) {
      this.addButton(bx + W - 130, by + btnRowY, 114, 'Entregar', TXT_DONE, () => {
        if (this.dialogNpcId) this.cb.onTurnIn(q.id, this.dialogNpcId);
      });
    } else if (!alreadyActive) {
      this.addButton(bx + W - 130, by + btnRowY, 114, 'Aceptar', TXT_GOLD, () => {
        if (this.dialogNpcId) this.cb.onAccept(q.id, this.dialogNpcId);
        this.closeDialog();
      });
    }
    // Botón cerrar/cancelar siempre.
    this.addButton(bx + 16, by + btnRowY, 100, 'Cerrar', TXT_DIM, () => this.closeDialog());

    const close = createCloseButton(this.scene, bx + W - 14, by + 14, 12);
    if (close) {
      close.setScrollFactor(0).setDepth(DEPTH + 1);
      close.on('pointerup', () => this.closeDialog());
      this.dialog.add(close);
    }
  }

  private closeDialog(): void {
    if (this.dialog) {
      this.dialog.destroy(true);
      this.dialog = null;
    }
    this.dialogQuest = null;
    this.dialogNpcId = null;
  }

  // =========================================================================
  // QUEST LOG (toggle)
  // =========================================================================

  public toggleLog(): void {
    if (this.log) {
      this.closeLog();
    } else {
      this.openLog();
    }
  }

  private openLog(): void {
    this.log = this.scene.add.container(0, 0).setDepth(DEPTH).setScrollFactor(0);
    // TODO(debug): si no hay quests reales, elegir una quest fake al azar para ver el render.
    if (this.quests.length === 0) {
      const defs = Object.values(QUEST_DEFINITIONS);
      this.debugQuest =
        defs.length > 0
          ? this.buildFakeQuest(defs[Math.floor(Math.random() * defs.length)], Math.random() < 0.5)
          : null;
    } else {
      this.debugQuest = null;
    }
    this.logSelectedId = this.displayQuests()[0]?.id ?? null;
    this.rebuildLog();
    // La quest fake de debug puede ser pineable → reflejarla en el panel flotante.
    this.refreshPinnedPanel();
  }

  private closeLog(): void {
    if (this.log) {
      this.log.destroy(true);
      this.log = null;
    }
    this.debugQuest = null;
    // Al cerrar el log, la fake de debug desaparece de displayQuests → re-render.
    this.refreshPinnedPanel();
  }

  /** Quests a renderizar en el log: las reales si hay, si no la fake de debug. */
  private displayQuests(): QuestView[] {
    if (this.quests.length > 0) return this.quests;
    return this.debugQuest ? [this.debugQuest] : [];
  }

  private rebuildLog(): void {
    if (!this.log) return;
    this.log.removeAll(true);

    const display = this.displayQuests();
    if (!display.find((q) => q.id === this.logSelectedId)) {
      this.logSelectedId = display[0]?.id ?? null;
    }

    const winX = Math.round(this.scene.scale.width / 2 - WIN_W / 2);
    const winY = Math.round(this.scene.scale.height / 2 - WIN_H / 2);
    this.logBaseX = winX;
    this.logBaseY = winY;

    // --- Frame de ventana (c_full_header) — handle de drag (header/márgenes) ---
    const frame = this.scene.add
      .image(winX, winY, 'c_full_header')
      .setOrigin(0, 0)
      .setDisplaySize(WIN_W, WIN_H)
      .setScrollFactor(0)
      .setInteractive({ useHandCursor: false });
    frame.on('pointerdown', (p: Phaser.Input.Pointer) => {
      if (!this.log) return;
      this.logDragging = true;
      this.logDragDX = p.x - this.log.x;
      this.logDragDY = p.y - this.log.y;
    });
    this.log.add(frame);

    // --- Header: icono + título "QUESTS" + botón de cierre ---
    const headerCy = winY + Math.round(HEADER_H * 0.54); // centro de la barra del header (tuneable)
    const icon = createUIIcon(this.scene, winX + BORDER + 12, headerCy, 'quest', 22);
    let titleX = winX + BORDER + 12;
    if (icon) {
      icon.setTint(0xffe9a8);
      this.log.add(icon);
      titleX = winX + BORDER + 30;
    }
    const title = this.scene.add
      .text(titleX, headerCy, 'QUESTS', withGameFont({ fontSize: '18px', color: TXT_TITLE, fontStyle: 'bold' }))
      .setOrigin(0, 0.5)
      .setScrollFactor(0);
    title.setShadow(0, 1, '#000000', 2, false, true);
    this.log.add(title);

    const closeSize = Math.round(HEADER_H * 0.62);
    const closeBtn = this.scene.add
      .image(winX + WIN_W - BORDER - closeSize / 2 - 2, headerCy, 'c_header_close')
      .setScrollFactor(0)
      .setInteractive({ useHandCursor: true });
    const setClose = (key: string): void => {
      closeBtn.setTexture(key);
      closeBtn.setDisplaySize(closeSize, closeSize);
    };
    setClose('c_header_close');
    closeBtn.on('pointerover', () => setClose('c_header_close-HOVER'));
    closeBtn.on('pointerout', () => setClose('c_header_close'));
    closeBtn.on('pointerdown', () => setClose('c_header_close-PUSH'));
    closeBtn.on('pointerup', () => {
      setClose('c_header_close-HOVER');
      this.closeLog();
    });
    this.log.add(closeBtn);

    // --- Regiones del cuerpo ---
    const contentY0 = winY + HEADER_H + 6;
    const contentY1 = winY + WIN_H - BORDER;
    const listX0 = winX + BORDER;

    // Panel inset de la columna izquierda (la lista), de arriba a abajo, para que
    // se lea como una columna definida que llega hasta el separador.
    const listPanel = this.scene.add
      .rectangle(listX0, contentY0, LIST_W, contentY1 - contentY0, 0x000000, 0.28)
      .setOrigin(0, 0)
      .setStrokeStyle(1, COLOR_BORDER, 0.5)
      .setScrollFactor(0);
    this.log.add(listPanel);

    // --- Lista (columna izquierda) ---
    if (display.length === 0) {
      const empty = this.scene.add
        .text(listX0 + 8, contentY0 + 10, 'No tenés misiones activas.', withGameFont({ fontSize: '12px', color: TXT_DIM }))
        .setScrollFactor(0);
      this.log.add(empty);
    }
    let ry = contentY0 + 6;
    for (const q of display) {
      this.buildListRow(q, listX0 + 4, ry, LIST_W - 8);
      ry += ROW_H + ROW_GAP;
    }

    // --- Separador vertical lista ↔ detalle (en la costura entre ambos paneles) ---
    const sepX = listX0 + LIST_W + SEP_W / 2;
    if (this.scene.textures.exists('container_vertical_seperator')) {
      const sep = this.scene.add
        .image(sepX, (contentY0 + contentY1) / 2, 'container_vertical_seperator')
        .setDisplaySize(SEP_W, contentY1 - contentY0)
        .setScrollFactor(0);
      this.log.add(sep);
    }

    // --- Panel inset de la columna derecha (detalle), contiguo al separador ---
    const detPanelX0 = listX0 + LIST_W + SEP_W;
    const detX1 = winX + WIN_W - BORDER;
    const detPanel = this.scene.add
      .rectangle(detPanelX0, contentY0, detX1 - detPanelX0, contentY1 - contentY0, 0x000000, 0.28)
      .setOrigin(0, 0)
      .setStrokeStyle(1, COLOR_BORDER, 0.5)
      .setScrollFactor(0);
    this.log.add(detPanel);

    // --- Detalle (columna derecha) — con padding interno ---
    const detX0 = detPanelX0 + 12;
    this.buildDetail(display.find((q) => q.id === this.logSelectedId) ?? null, detX0, contentY0, detX1 - 12 - detX0, contentY1);
  }

  /** Una fila de la lista de quests (fondo + checkbox + nombre). */
  private buildListRow(q: QuestView, x: number, y: number, w: number): void {
    if (!this.log) return;
    const selected = q.id === this.logSelectedId;
    const cy = y + ROW_H / 2;

    const bgKey = selected && this.scene.textures.exists('quest_list_row_ACTIVE')
      ? 'quest_list_row_ACTIVE'
      : 'quest_list_row_bg';
    const rowBg = this.scene.add
      .image(x, y, bgKey)
      .setOrigin(0, 0)
      .setDisplaySize(w, ROW_H)
      .setScrollFactor(0)
      .setInteractive({ useHandCursor: true });
    this.log.add(rowBg);

    let hover: Phaser.GameObjects.Image | null = null;
    if (!selected && this.scene.textures.exists('quest_list_row-HOVER')) {
      hover = this.scene.add
        .image(x, y, 'quest_list_row-HOVER')
        .setOrigin(0, 0)
        .setDisplaySize(w, ROW_H)
        .setScrollFactor(0)
        .setVisible(false);
      this.log.add(hover);
    }
    rowBg.on('pointerover', () => hover?.setVisible(true));
    rowBg.on('pointerout', () => hover?.setVisible(false));
    rowBg.on('pointerup', () => {
      this.logSelectedId = q.id;
      this.rebuildLog();
    });

    const cbKey = q.status === 'ready' ? 'quest_list_row_checkbox_selected' : 'quest_list_row_checkbox';
    if (this.scene.textures.exists(cbKey)) {
      const cb = this.scene.add.image(x + 14, cy, cbKey).setDisplaySize(CHECKBOX, CHECKBOX).setScrollFactor(0);
      this.log.add(cb);
    }

    const nameX = x + 14 + CHECKBOX + 10;
    const name = this.scene.add
      .text(nameX, cy, q.nameEs, withGameFont({
        fontSize: '12px',
        color: selected ? TXT_TITLE : TXT,
        fontStyle: 'bold',
        // Deja lugar a la derecha para el botón de fijar (pin).
        wordWrap: { width: Math.max(40, w - (nameX - x) - 40) },
      }))
      .setOrigin(0, 0.5)
      .setScrollFactor(0);
    this.log.add(name);

    // --- Botón de fijar (pin) al extremo derecho de la fila. Lleno si fijada. ---
    const pinned = this.isPinned(q.id);
    const pin = createUIIcon(this.scene, x + w - 15, cy, 'pin_flag', 18);
    if (pin) {
      pin.setInteractive({ useHandCursor: true });
      const applyPinLook = (): void => {
        pin.setAlpha(pinned ? 1 : 0.4);
        if (pinned) pin.clearTint();
        else pin.setTint(0x9a9a9a);
      };
      applyPinLook();
      pin.on('pointerover', () => pin.setAlpha(pinned ? 1 : 0.75));
      pin.on('pointerout', () => applyPinLook());
      pin.on('pointerup', (p: Phaser.Input.Pointer) => {
        this.togglePin(q.id);
        p.event.stopPropagation();
      });
      this.log.add(pin);
    }
  }

  /** Panel de detalle de la quest seleccionada (título, descripción, objetivos, recompensas). */
  private buildDetail(q: QuestView | null, x: number, y0: number, w: number, y1: number): void {
    if (!this.log) return;

    // Fondo sutil de la zona de descripción (overlay claro, additive a baja alpha).
    if (this.scene.textures.exists('quest_description_container_bg_effect')) {
      const bgfx = this.scene.add
        .image(x, y0, 'quest_description_container_bg_effect')
        .setOrigin(0, 0)
        .setDisplaySize(w, y1 - y0)
        .setScrollFactor(0)
        .setAlpha(0.05)
        .setBlendMode(Phaser.BlendModes.ADD);
      this.log.add(bgfx);
    }

    if (!q) return;

    const rewardsH = 50;
    const sepTop = y1 - rewardsH - 6; // borde superior del separador de recompensas

    // Reset del scroll de objetivos al cambiar de quest seleccionada.
    if (this.objScrollSelId !== q.id) {
      this.objScroll = 0;
      this.objScrollSelId = q.id;
    }

    // --- Título (fijo) ---
    let dy = y0 + 8;
    const title = this.scene.add
      .text(x, dy, q.nameEs, withGameFont({ fontSize: '19px', color: TXT_TITLE, fontStyle: 'bold', wordWrap: { width: w } }))
      .setScrollFactor(0);
    this.log.add(title);
    dy += title.height + 8;

    // --- Descripción (fija, máx 3 líneas) ---
    const desc = this.scene.add
      .text(x, dy, q.descriptionEs, withGameFont({ fontSize: '12px', color: TXT_DIM, lineSpacing: 3, wordWrap: { width: w } }))
      .setScrollFactor(0);
    this.clampTextLines(desc, q.descriptionEs, 3);
    this.log.add(desc);
    dy += desc.height + 12;

    // --- Objetivos: región scrollable (windowing por índice; título/desc quedan fijos) ---
    const ICON = 32; // tamaño real del gráfico
    const SLOT = ICON + 12; // recuadro = icono + 6px de padding por lado
    const OBJ_GAP = 8;
    const rowH = SLOT + OBJ_GAP;
    const regionTop = dy;
    const regionBottom = sepTop - 8;
    const regionH = Math.max(rowH, regionBottom - regionTop);
    const total = q.objectives.length;
    const visible = Math.max(1, Math.floor((regionH + OBJ_GAP) / rowH));
    const maxOffset = Math.max(0, total - visible);
    this.objScroll = Phaser.Math.Clamp(this.objScroll, 0, maxOffset);

    const hasScroll = total > visible;
    const sbW = 6; // ancho del scrollbar
    const listW = w - (hasScroll ? sbW + 8 : 0); // deja lugar al scrollbar

    let oy = regionTop;
    for (let i = this.objScroll; i < Math.min(total, this.objScroll + visible); i++) {
      this.buildObjectiveRow(q.objectives[i], x, oy, listW, SLOT, ICON);
      oy += rowH;
    }

    // Métricas para rueda / thumb (coords base, sin offset de drag).
    this.scrollMetrics = hasScroll
      ? { top: regionTop, bottom: regionBottom, left: x, right: x + w, total, visible }
      : null;
    if (hasScroll) {
      this.buildObjScrollbar(x + w - sbW, regionTop, sbW, regionH, total, visible);
    }

    // Separador + franja de recompensas, pegados al fondo del panel.
    if (this.scene.textures.exists('open_quest_seperator')) {
      const sep = this.scene.add
        .image(x, y1 - rewardsH - 6, 'open_quest_seperator')
        .setOrigin(0, 0)
        .setDisplaySize(w, 8)
        .setScrollFactor(0)
        .setAlpha(0.7);
      this.log.add(sep);
    }
    // La quest fake de debug no manda ABANDON al server: solo cierra el log.
    const onAbandon = q.id.startsWith('debug_') ? (): void => this.closeLog() : (): void => this.cb.onAbandon(q.id);
    this.buildRewards(q.rewards, x, y1 - rewardsH, w, rewardsH, onAbandon);
  }

  /**
   * Una fila de objetivo: slot con gráfico (sprite NPC / icono item) + etiqueta con
   * jerarquía tipográfica (verbo normal + nombre en negrita) y, a la derecha, el
   * progreso y el ✔ verde si está completo.
   */
  private buildObjectiveRow(o: QuestObjectiveView, x: number, oy: number, w: number, SLOT: number, ICON: number): void {
    if (!this.log) return;
    const slotCy = oy + SLOT / 2;
    const slotCx = x + SLOT / 2;

    const slot = this.scene.add
      .rectangle(x, oy, SLOT, SLOT, 0x000000, 0.85)
      .setOrigin(0, 0)
      .setStrokeStyle(1, COLOR_BORDER, 0.9)
      .setScrollFactor(0);
    this.log.add(slot);

    let placed = false;
    if ((o.type === 'kill' || o.type === 'talk') && o.npcId) {
      placed = this.addNpcMini(o.npcId, slotCx, slotCy, ICON, o.complete);
    } else if (o.type === 'collect' && o.itemId) {
      placed = this.addItemMini(o.itemId, slotCx, slotCy, ICON, o.complete);
    }
    if (!placed) {
      const ic = createUIIcon(this.scene, slotCx, slotCy, OBJECTIVE_ICON[o.type], ICON - 6);
      if (ic) {
        if (o.complete) ic.setTint(0x77dd77);
        this.log.add(ic);
      }
    }

    // Layout en dos renglones dentro de la fila: arriba etiqueta + progreso/✔ (a la
    // derecha); abajo una barra de progreso que cruza el ancho útil.
    const labelX = x + SLOT + 12;
    const labelRight = x + w;
    const topY = oy + SLOT * 0.32;
    const barY = oy + SLOT * 0.68;

    // Progreso/✔ alineados a la derecha del renglón superior. Se dibuja primero para
    // saber cuánto espacio dejarle al nombre (evita que se encimen).
    let rightW = 0;
    if (o.complete) {
      const tick = this.scene.add
        .text(labelRight, topY, '✔', withGameFont({ fontSize: '13px', color: TXT_DONE, fontStyle: 'bold' }))
        .setOrigin(1, 0.5)
        .setScrollFactor(0);
      this.log.add(tick);
      rightW = tick.width + 8;
    } else if (o.target > 1) {
      const ct = this.scene.add
        .text(labelRight, topY, `${o.current}/${o.target}`, withGameFont({ fontSize: '12px', color: TXT_DIM }))
        .setOrigin(1, 0.5)
        .setScrollFactor(0);
      this.log.add(ct);
      rightW = ct.width + 8;
    }

    // Etiqueta: prefijo (normal, dim) + nombre (negrita), acotada para no pisar el progreso.
    const { prefix, name } = splitObjectiveLabel(o.descEs);
    const labelMaxRight = labelRight - rightW;
    let cx = labelX;
    if (prefix) {
      const pt = this.scene.add
        .text(cx, topY, prefix + ' ', withGameFont({ fontSize: '12px', color: TXT_DIM }))
        .setOrigin(0, 0.5)
        .setScrollFactor(0);
      this.log.add(pt);
      cx += pt.width;
    }
    const nt = this.scene.add
      .text(cx, topY, name, withGameFont({
        fontSize: '13px',
        color: o.complete ? TXT_DONE : TXT,
        fontStyle: 'bold',
        wordWrap: { width: Math.max(40, labelMaxRight - cx) },
      }))
      .setOrigin(0, 0.5)
      .setScrollFactor(0);
    // Truncar a 1 línea si el nombre es muy largo (mantiene la fila de alto fijo).
    if (nt.getWrappedText(name).length > 1) this.clampTextLines(nt, name, 1);
    this.log.add(nt);

    // Barra de progreso del objetivo (track + fill proporcional). Verde si completo,
    // dorado si en progreso. Los binarios (talk/reach, target 1) quedan 0% ó 100%.
    const frac = o.target > 0 ? Phaser.Math.Clamp(o.current / o.target, 0, 1) : (o.complete ? 1 : 0);
    const barW = Math.max(0, labelRight - labelX);
    const barH = 7;
    const track = this.scene.add
      .rectangle(labelX, barY, barW, barH, 0x000000, 0.55)
      .setOrigin(0, 0.5)
      .setStrokeStyle(1, COLOR_BORDER, 0.6)
      .setScrollFactor(0);
    this.log.add(track);
    const fillW = barW * frac;
    if (fillW > 0) {
      const fill = this.scene.add
        .rectangle(labelX, barY, fillW, barH, o.complete ? 0x77dd77 : 0xffd56b, 0.95)
        .setOrigin(0, 0.5)
        .setScrollFactor(0);
      this.log.add(fill);
    }
  }

  /** Scrollbar (track + thumb arrastrable) de la sección de objetivos. */
  private buildObjScrollbar(x: number, y: number, w: number, h: number, total: number, visible: number): void {
    if (!this.log) return;
    const track = this.scene.add
      .rectangle(x, y, w, h, 0x000000, 0.4)
      .setOrigin(0, 0)
      .setStrokeStyle(1, COLOR_BORDER, 0.4)
      .setScrollFactor(0);
    this.log.add(track);

    const max = Math.max(1, total - visible);
    const thumbH = Math.max(24, h * (visible / total));
    const travel = h - thumbH;
    const thumbY = y + travel * (this.objScroll / max);
    const thumb = this.scene.add
      .rectangle(x, thumbY, w, thumbH, COLOR_BORDER, 0.85)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setInteractive({ useHandCursor: true });
    thumb.on('pointerdown', (p: Phaser.Input.Pointer) => {
      if (!this.log) return;
      this.thumbDragging = true;
      this.thumbGrabDY = p.y - this.log.y - thumbY;
      p.event.stopPropagation();
    });
    this.log.add(thumb);
  }

  /** Trunca un Text (con wordWrap) a `maxLines`, agregando '…' si sobra. */
  private clampTextLines(t: Phaser.GameObjects.Text, full: string, maxLines: number): void {
    if (t.getWrappedText(full).length <= maxLines) return;
    let kept = full;
    while (kept.length > 0) {
      kept = kept.slice(0, -1);
      const candidate = kept.replace(/\s+\S*$/, '').trim() + '…';
      if (t.getWrappedText(candidate).length <= maxLines) {
        t.setText(candidate);
        return;
      }
    }
    t.setText('…');
  }

  /** Franja de recompensas (coin = oro, crystal = honor, xp) + botón Abandonar a la derecha. */
  private buildRewards(
    r: QuestRewards,
    x: number,
    y: number,
    w: number,
    h: number,
    onAbandon: () => void
  ): void {
    if (!this.log) return;
    if (this.scene.textures.exists('open_quest_rewards_bg')) {
      const bg = this.scene.add
        .image(x, y, 'open_quest_rewards_bg')
        .setOrigin(0, 0)
        .setDisplaySize(w, h)
        .setScrollFactor(0)
        .setAlpha(0.12)
        .setBlendMode(Phaser.BlendModes.ADD);
      this.log.add(bg);
    }
    const cy = y + h / 2;
    let ix = x + 10;
    const addReward = (iconKey: string, amount: number | undefined, color: string): void => {
      if (!amount) return;
      if (this.scene.textures.exists(iconKey)) {
        const ic = this.scene.add.image(ix, cy, iconKey).setOrigin(0, 0.5).setScrollFactor(0);
        ic.setScale(24 / Math.max(ic.width, ic.height));
        this.log!.add(ic);
        ix += ic.displayWidth + 6;
      }
      const t = this.scene.add
        .text(ix, cy, String(amount), withGameFont({ fontSize: '14px', color, fontStyle: 'bold' }))
        .setOrigin(0, 0.5)
        .setScrollFactor(0);
      this.log!.add(t);
      ix += t.width + 28;
    };
    addReward('quest_reward_coin', r.gold, TXT);
    addReward('quest_reward_crystal', r.honor, TXT);
    addReward('quest_reward_xp', r.xp, TXT_DONE);

    // Botón "Abandonar" en el extremo derecho de la franja (a la derecha del XP),
    // con padding al borde y borde del mismo color que su tipografía.
    const btnW = 100;
    const btnH = 28;
    this.addButton(x + w - btnW - 14, cy - btnH / 2, btnW, 'Abandonar', '#dd6666', onAbandon, btnH, 0xdd6666);
  }

  /**
   * Mini-retrato del NPC del objetivo (primer frame mirando abajo), encuadrado por
   * su contenido opaco dentro de `box`. Usa la cabeza si el NPC la tiene (cara),
   * si no el cuerpo (criaturas). Devuelve false si no se pudo (caller cae al genérico).
   */
  private addNpcMini(npcId: string, cx: number, cy: number, box: number, complete: boolean): boolean {
    if (!this.log) return false;
    const tpl = getNPCTemplate(npcId);
    if (!tpl) return false;
    const headId = tpl.headId ?? 0;
    const bodyId = tpl.bodyId ?? 1;
    let key: string;
    let frame: number;
    if (headId > 0 && this.scene.textures.exists(getHeadSpriteKey(headId))) {
      key = getHeadSpriteKey(headId);
      frame = getHeadFrameForDirection(Direction.DOWN);
    } else if (this.scene.textures.exists(getBodySpriteKey(bodyId))) {
      key = getBodySpriteKey(bodyId);
      frame = getBodyIdleFrame(Direction.DOWN, bodyId);
    } else {
      return false;
    }
    const spr = this.scene.add.sprite(cx, cy, key, frame).setOrigin(0.5).setScrollFactor(0);
    const bb = this.contentBBox(key, frame);
    const scale = bb
      ? box / Math.max(1, Math.max(bb.w, bb.h))
      : box / Math.max(1, Math.max(spr.width, spr.height));
    spr.setScale(scale);
    if (bb) {
      // Centrar por el contenido opaco (el frame trae padding transparente).
      spr.setPosition(cx - (bb.cx - bb.fw / 2) * scale, cy - (bb.cy - bb.fh / 2) * scale);
    }
    if (complete) spr.setTint(0x88ff88);
    this.log.add(spr);
    return true;
  }

  /** Icono del item del objetivo `collect` (textura `item_icon_<icon>`). */
  private addItemMini(itemId: string, cx: number, cy: number, box: number, complete: boolean): boolean {
    if (!this.log) return false;
    const tpl = getItemTemplate(itemId);
    const iconKey = tpl && this.scene.textures.exists(`item_icon_${tpl.icon}`)
      ? `item_icon_${tpl.icon}`
      : 'item_icon_noicon';
    if (!this.scene.textures.exists(iconKey)) return false;
    const spr = this.scene.add.sprite(cx, cy, iconKey).setOrigin(0.5).setDisplaySize(box, box).setScrollFactor(0);
    if (complete) spr.setTint(0x88ff88);
    this.log.add(spr);
    return true;
  }

  /** Bbox del contenido opaco de un frame (px del frame), cacheado. (Cf. TargetPanel.) */
  private contentBBox(
    textureKey: string,
    frameIndex: number
  ): { cx: number; cy: number; w: number; h: number; fw: number; fh: number } | null {
    const cacheKey = `${textureKey}#${frameIndex}`;
    const cached = this.bboxCache.get(cacheKey);
    if (cached) return cached;
    const tex = this.scene.textures.get(textureKey);
    const frame = tex.get(frameIndex);
    if (!frame) return null;
    const fw = frame.cutWidth;
    const fh = frame.cutHeight;
    const canvas = document.createElement('canvas');
    canvas.width = fw;
    canvas.height = fh;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    const src = tex.getSourceImage() as CanvasImageSource;
    ctx.drawImage(src, frame.cutX, frame.cutY, fw, fh, 0, 0, fw, fh);
    const data = ctx.getImageData(0, 0, fw, fh).data;
    let x0 = fw, y0 = fh, x1 = -1, y1 = -1;
    for (let yy = 0; yy < fh; yy++) {
      for (let xx = 0; xx < fw; xx++) {
        if (data[(yy * fw + xx) * 4 + 3] > 20) {
          if (xx < x0) x0 = xx;
          if (xx > x1) x1 = xx;
          if (yy < y0) y0 = yy;
          if (yy > y1) y1 = yy;
        }
      }
    }
    const res =
      x1 < 0
        ? { cx: fw / 2, cy: fh / 2, w: fw, h: fh, fw, fh }
        : { cx: (x0 + x1 + 1) / 2, cy: (y0 + y1 + 1) / 2, w: x1 - x0 + 1, h: y1 - y0 + 1, fw, fh };
    this.bboxCache.set(cacheKey, res);
    return res;
  }

  /**
   * TODO(debug): construye un QuestView fake a partir de una definición real para
   * poder ver el render del log sin aceptar una quest in-game. QUITAR cuando esté
   * validado el look. `ready` = todos los objetivos completos (checkbox verde).
   */
  private buildFakeQuest(def: (typeof QUEST_DEFINITIONS)[string], ready: boolean): QuestView {
    const objectives: QuestObjectiveView[] = def.objectives.map((o, idx) => {
      const target = objectiveTarget(o);
      const complete = ready || idx < 2; // primeros 2 completos para ver ✔ mezclado
      return {
        type: o.type,
        descEs: o.descEs ?? defaultObjectiveLabel(o),
        current: complete ? target : Math.floor(target / 2),
        target,
        complete,
        active: true,
        blocking: !!o.blocking,
        npcId: o.type === 'kill' || o.type === 'talk' ? o.npcId : undefined,
        itemId: o.type === 'collect' ? o.itemId : undefined,
        areaId: o.type === 'reach' ? o.areaId : undefined,
      };
    });
    // TODO(debug): 2 objetivos extra para probar el scroll de la sección de objetivos.
    objectives.push(
      {
        type: 'talk',
        descEs: 'Hablar con el Capitán Aldric',
        current: 0,
        target: 1,
        complete: false,
        active: true,
        blocking: false,
        npcId: 'npc_quest_1',
      },
      {
        type: 'collect',
        descEs: 'Conseguir una espada',
        current: 1,
        target: 1,
        complete: true,
        active: true,
        blocking: false,
        itemId: 'weapon_sword_001',
      }
    );
    return {
      id: `debug_${def.id}${ready ? '_ready' : ''}`,
      nameEs: def.nameEs,
      descriptionEs: def.descriptionEs,
      status: ready ? 'ready' : 'active',
      objectives,
      // honor por defecto para ver el icono de cristal aunque la def no lo defina.
      rewards: { ...def.rewards, honor: def.rewards.honor ?? 50 },
    };
  }

  // =========================================================================
  // HELPERS
  // =========================================================================

  private addButton(
    x: number,
    y: number,
    w: number,
    label: string,
    color: string,
    onClick: () => void,
    height = 30,
    borderColor: number = COLOR_BORDER
  ): void {
    const target = this.dialog ?? this.log;
    if (!target) return;
    const h = height;
    const bg = this.scene.add
      .rectangle(x, y, w, h, 0x2a2418, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(1, borderColor, 1)
      .setScrollFactor(0)
      .setInteractive({ useHandCursor: true });
    const txt = this.scene.add
      .text(x + w / 2, y + h / 2, label, withGameFont({ fontSize: '13px', color }))
      .setOrigin(0.5, 0.5)
      .setScrollFactor(0);
    bg.on('pointerover', () => bg.setFillStyle(0x3a3220, 1));
    bg.on('pointerout', () => bg.setFillStyle(0x2a2418, 1));
    bg.on('pointerup', onClick);
    target.add([bg, txt]);
  }

  private rewardsLine(r: QuestRewards): string {
    const parts: string[] = [];
    if (r.xp) parts.push(`${r.xp} XP`);
    if (r.gold) parts.push(`${r.gold} oro`);
    if (r.honor) parts.push(`${r.honor} honor`);
    if (r.items && r.items.length) parts.push(r.items.map((i) => `${i.quantity}× ${i.itemId}`).join(', '));
    if (r.spells && r.spells.length) parts.push(`hechizo: ${r.spells.join(', ')}`);
    return parts.join('  ·  ');
  }

  private toast(title: string, sub: string): void {
    const cx = this.scene.scale.width / 2;
    const cont = this.scene.add.container(0, 0).setDepth(DEPTH + 2).setScrollFactor(0);
    const t1 = this.scene.add
      .text(cx, 90, title, withGameFont({ fontSize: '16px', color: TXT_GOLD, fontStyle: 'bold' }))
      .setOrigin(0.5, 0)
      .setScrollFactor(0);
    const t2 = this.scene.add
      .text(cx, 112, sub, withGameFont({ fontSize: '12px', color: TXT }))
      .setOrigin(0.5, 0)
      .setScrollFactor(0);
    cont.add([t1, t2]);
    this.scene.tweens.add({
      targets: cont,
      alpha: { from: 1, to: 0 },
      delay: 2200,
      duration: 800,
      onComplete: () => cont.destroy(true),
    });
  }

  private onResize(): void {
    if (this.dialog) this.rebuildDialog();
    if (this.log) this.rebuildLog();
  }

  public destroy(): void {
    this.closeDialog();
    this.closeLog();
    this.pinnedPanel.destroy();
  }
}
